import { ModelType, logger, parseKeyValueXml } from '@elizaos/core';
import type {
  Action,
  ActionExample,
  Content,
  HandlerCallback,
  IAgentRuntime,
  Memory,
  State,
} from '@elizaos/core';
import type { ZapperConfig } from '../types';
import {
  getZapperClient,
  initializeZapperClient,
  isZapperClientInitialized,
} from '../utils/zapperClient';

// Define proper interfaces for type safety
interface TokenEdge {
  node: {
    symbol: string;
    name: string;
    balance: string;
    balanceUSD: string;
    network: {
      name: string;
    };
    tokenAddress: string;
    price: number;
  };
}

interface ZapperTokenResponse {
  data?: {
    portfolioV2?: {
      tokenBalances?: {
        byToken?: {
          edges: TokenEdge[];
        };
      };
    };
  };
  errors?: Array<{
    message: string;
    locations?: Array<{ line: number; column: number }>;
    path?: string[];
  }>;
}

interface TokenPriceData {
  price: number;
  priceChange24h: number;
  marketCap: number;
  volume24h: number;
  symbol: string;
  name: string;
  network: string;
  tokenAddress: string;
}

export interface GetTokenPriceDataContent extends Content {
  symbol: string;
  network?: string;
  currency?: string;
}

const getTokenPriceDataTemplate = `Respond with an XML block containing only the extracted values. Use key-value pairs.

Example response:
<response>
    <symbol>ETH</symbol>
    <network>ethereum</network>
    <currency>USD</currency>
</response>

## Recent Messages

{{recentMessages}}

Given the recent messages, extract the following information about the current token price request:

Required fields:
- Symbol: The token symbol (ETH, BTC, etc.)
- Network: Optional network name (ethereum, polygon, arbitrum, optimism, base)
- Currency: Optional display currency (USD, EUR, etc.)

Respond with an XML block containing only the extracted values.`;

export const getTokenPriceDataAction: Action = {
  name: 'GET_TOKEN_PRICE_DATA',
  similes: [
    'GET_TOKEN_PRICE',
    'CHECK_PRICE',
    'TOKEN_PRICE',
    'PRICE_CHECK',
    'GET_PRICE',
    'CHECK_TOKEN',
    'TOKEN_VALUE',
  ],
  description: 'Get current token price data from Zapper Protocol',
  validate: async (runtime: IAgentRuntime, message: Memory) => {
    logger.debug('🔍 Starting GET_TOKEN_PRICE_DATA validation', {
      messageId: message.id,
      messageText: message.content.text
        ? message.content.text.substring(0, 100) + (message.content.text.length > 100 ? '...' : '')
        : 'No text content',
    });

    // Step 1: Validate API key configuration
    logger.debug('📋 Step 1: Checking API key configuration');
    const hasApiKey = runtime.getSetting('ZAPPER_API_KEY');
    if (!hasApiKey) {
      logger.error('❌ ZAPPER_API_KEY not configured - validation failed');
      return false;
    }
    logger.debug('✅ API key configuration validated');

    // Step 2: Process message text
    const text = message.content.text?.toLowerCase() || '';
    logger.debug('📝 Step 2: Processing message text', {
      originalLength: message.content.text?.length || 0,
      processedText: text.substring(0, 200) + (text.length > 200 ? '...' : ''),
    });

    // Step 3: Check for price keywords
    logger.debug('🔑 Step 3: Checking for price keywords');
    const priceKeywords = [
      'current price',
      'token price',
      'price of',
      'cost of',
      'value of',
      'worth of',
      'check price',
      'get price',
      'what is',
      'how much',
    ];

    const hasKeyword = priceKeywords.some((keyword) => {
      const found = text.includes(keyword);
      if (found) {
        logger.debug(`✅ Found price keyword: "${keyword}"`);
      }
      return found;
    });

    if (!hasKeyword) {
      logger.debug('⚠️ No price keywords found in message', { checkedKeywords: priceKeywords });
    }

    // Step 4: Check for token symbols
    logger.debug('🪙 Step 4: Checking for token symbols');
    const tokenSymbolRegex =
      /\b(btc|eth|bnb|ada|sol|matic|avax|link|uni|usdc|usdt|weth|dai|aave|comp|mkr|snx|1inch)\b/i;
    const hasTokenSymbol = tokenSymbolRegex.test(text);

    if (hasTokenSymbol) {
      const matches = text.match(tokenSymbolRegex);
      logger.debug('✅ Token symbol(s) found', { symbols: matches });
    } else {
      logger.debug('⚠️ No recognized token symbols found in message');
    }

    // Step 5: Final validation result
    const isValid = hasKeyword && hasTokenSymbol;
    logger.debug('🎯 Step 5: Final validation result', {
      hasKeyword,
      hasTokenSymbol,
      isValid,
      reason: !isValid
        ? hasKeyword
          ? 'Missing token symbol'
          : hasTokenSymbol
            ? 'Missing price keyword'
            : 'Missing both keyword and symbol'
        : 'Validation successful',
    });

    return isValid;
  },
  handler: async (
    runtime: IAgentRuntime,
    message: Memory,
    state: State | undefined,
    _options: { [key: string]: unknown } | undefined,
    callback?: HandlerCallback
  ) => {
    const startTime = Date.now();
    logger.log('🚀 Starting GET_TOKEN_PRICE_DATA handler', {
      messageId: message.id,
      hasState: !!state,
      timestamp: new Date().toISOString(),
    });

    try {
      // Step 1: State composition
      logger.debug('📊 Step 1: Composing state for analysis');
      let currentState = state;
      if (!currentState) {
        logger.debug('🔄 No existing state - composing new state from message');
        currentState = await runtime.composeState(message);
        logger.debug('✅ New state composed successfully');
      } else {
        logger.debug('🔄 Existing state found - composing with recent messages');
        currentState = await runtime.composeState(message, ['RECENT_MESSAGES']);
        logger.debug('✅ State updated with recent messages');
      }

      // Step 2: Prompt preparation
      logger.debug('📝 Step 2: Preparing prompt for AI model');
      const recentMessagesData = currentState.recentMessagesData || '';
      logger.debug('📋 Recent messages data prepared', {
        hasRecentMessages: !!recentMessagesData,
        dataLength: recentMessagesData.length,
      });

      const prompt = getTokenPriceDataTemplate.replace('{{recentMessages}}', recentMessagesData);
      logger.debug('✅ Prompt template prepared successfully', {
        promptLength: prompt.length,
      });

      // Step 3: AI model processing
      logger.debug('🤖 Step 3: Processing request with AI model');
      const modelStartTime = Date.now();
      const result = await runtime.useModel(ModelType.TEXT_SMALL, {
        prompt,
        stopSequences: [],
      });
      const modelDuration = Date.now() - modelStartTime;
      logger.debug('✅ AI model processing completed', {
        duration: `${modelDuration}ms`,
        resultLength: result?.length || 0,
      });

      // Step 4: Content parsing
      logger.debug('🔍 Step 4: Parsing AI model response');
      const content = parseKeyValueXml(result);
      logger.debug('📊 Content parsed from AI response', {
        parsedContent: content,
        hasSymbol: !!content?.symbol,
        hasNetwork: !!content?.network,
        hasCurrency: !!content?.currency,
      });

      // Step 5: Content validation
      logger.debug('✅ Step 5: Validating parsed content');
      if (!isGetTokenPriceDataContent(runtime, content)) {
        logger.error('❌ Content validation failed - invalid token price request format', {
          content,
          validationReason: 'Failed isGetTokenPriceDataContent check',
        });
        callback?.({
          text: 'Unable to process token price request. Please provide a valid token symbol and specify what type of price data you need.',
          content: { error: 'Invalid content' },
        });
        return;
      }
      logger.debug('✅ Content validation successful');

      // Step 6: Zapper configuration
      logger.debug('⚙️ Step 6: Setting up Zapper configuration');
      const zapperConfig: ZapperConfig = {
        apiKey: runtime.getSetting('ZAPPER_API_KEY'),
        apiUrl: runtime.getSetting('ZAPPER_API_URL') || 'https://public.zapper.xyz/graphql',
      };
      logger.debug('✅ Zapper configuration prepared', {
        hasApiKey: !!zapperConfig.apiKey,
        apiUrl: zapperConfig.apiUrl,
      });

      // Step 7: Price data fetching
      logger.debug('💰 Step 7: Fetching current price data');
      await handleCurrentPrice(zapperConfig, content, callback);

      const totalDuration = Date.now() - startTime;
      logger.log('🎉 GET_TOKEN_PRICE_DATA handler completed successfully', {
        totalDuration: `${totalDuration}ms`,
        symbol: content.symbol,
        network: content.network || 'ethereum',
      });
    } catch (error) {
      const errorDuration = Date.now() - startTime;
      logger.error('💥 GET_TOKEN_PRICE_DATA handler failed with error', {
        error: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined,
        duration: `${errorDuration}ms`,
        messageId: message.id,
        errorType: error?.constructor?.name || 'Unknown',
      });

      // Provide more specific error messages based on error type
      let errorMessage = 'Failed to fetch token price data. Please try again.';
      const errorDetails = error instanceof Error ? error.message : String(error);

      if (error instanceof Error) {
        if (error.message.includes('timeout')) {
          errorMessage =
            'Request timed out while fetching token price. The Zapper API may be slow. Please try again.';
          logger.error('⏰ Handler failed due to timeout');
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMessage =
            'Network error occurred while fetching token price. Please check your connection and try again.';
          logger.error('🌐 Handler failed due to network error');
        } else if (
          error.message.includes('authentication') ||
          error.message.includes('unauthorized') ||
          error.message.includes('api key')
        ) {
          errorMessage = 'Authentication failed. Please check the Zapper API configuration.';
          logger.error('🔐 Handler failed due to authentication error');
        } else if (error.message.includes('rate limit')) {
          errorMessage = 'API rate limit exceeded. Please wait a moment and try again.';
          logger.error('📊 Handler failed due to rate limiting');
        } else if (error.message.includes('Invalid content')) {
          errorMessage =
            'Unable to understand the token price request. Please provide a clear token symbol.';
          logger.error('📝 Handler failed due to content parsing error');
        }
      }

      callback?.({
        text: errorMessage,
        content: {
          error: errorDetails,
          errorType: error?.constructor?.name || 'Unknown',
          duration: `${errorDuration}ms`,
        },
      });
    }
  },
  examples: [
    [
      {
        name: '{{user1}}',
        content: {
          text: 'What is the current price of ETH?',
        },
      },
      {
        name: '{{user2}}',
        content: {
          text: 'Current token price retrieved',
          action: 'GET_TOKEN_PRICE_DATA',
        },
      },
    ],
    [
      {
        name: '{{user1}}',
        content: {
          text: 'Check BTC price on polygon',
        },
      },
      {
        name: '{{user2}}',
        content: {
          text: 'Token price data retrieved',
          action: 'GET_TOKEN_PRICE_DATA',
        },
      },
    ],
  ] as ActionExample[][],
};

function isGetTokenPriceDataContent(
  _runtime: IAgentRuntime,
  content: unknown
): content is GetTokenPriceDataContent {
  logger.debug('🔍 Validating content structure for GetTokenPriceDataContent', {
    content: content,
    contentType: typeof content,
  });

  // Check if content exists
  if (!content) {
    logger.debug('❌ Content validation failed: content is null or undefined');
    return false;
  }

  if (typeof content !== 'object') {
    logger.debug('❌ Content validation failed: content is not an object');
    return false;
  }

  const contentObj = content as Record<string, unknown>;

  // Check symbol property
  if (typeof contentObj.symbol !== 'string') {
    logger.debug('❌ Content validation failed: symbol is not a string', {
      symbolType: typeof contentObj.symbol,
      symbolValue: contentObj.symbol,
    });
    return false;
  }

  if (contentObj.symbol.length === 0) {
    logger.debug('❌ Content validation failed: symbol is empty string');
    return false;
  }

  // Check optional network property
  if (contentObj.network !== undefined && typeof contentObj.network !== 'string') {
    logger.debug('❌ Content validation failed: network is not a string', {
      networkType: typeof contentObj.network,
      networkValue: contentObj.network,
    });
    return false;
  }

  // Check optional currency property
  if (contentObj.currency !== undefined && typeof contentObj.currency !== 'string') {
    logger.debug('❌ Content validation failed: currency is not a string', {
      currencyType: typeof contentObj.currency,
      currencyValue: contentObj.currency,
    });
    return false;
  }

  logger.debug('✅ Content validation successful', {
    symbol: contentObj.symbol,
    network: contentObj.network || 'not specified',
    currency: contentObj.currency || 'not specified',
  });

  return true;
}

async function handleCurrentPrice(
  config: ZapperConfig,
  content: GetTokenPriceDataContent,
  callback?: HandlerCallback
) {
  const startTime = Date.now();
  const network = content.network || 'ethereum';
  const currency = content.currency || 'USD';

  logger.debug('💰 Starting price data handling', {
    symbol: content.symbol,
    network,
    currency,
    hasCallback: !!callback,
  });

  try {
    logger.debug('🔍 Fetching token price from Zapper API');
    const tokenPriceData = await fetchTokenPrice(config, content.symbol, network);
    const fetchDuration = Date.now() - startTime;

    if (!tokenPriceData) {
      logger.warn('⚠️ No token price data found', {
        symbol: content.symbol,
        network,
        fetchDuration: `${fetchDuration}ms`,
      });

      callback?.({
        text: `No price data found for ${content.symbol} on ${network}`,
        content: { symbol: content.symbol, network },
      });
      return;
    }

    logger.debug('✅ Token price data retrieved successfully', {
      symbol: content.symbol,
      price: tokenPriceData.price,
      tokenName: tokenPriceData.name,
      network: tokenPriceData.network,
      tokenAddress: tokenPriceData.tokenAddress,
      fetchDuration: `${fetchDuration}ms`,
    });

    // Format response text
    const responseText = `💰 ${content.symbol.toUpperCase()} Price:

🔸 Current Price: $${tokenPriceData.price.toFixed(6)}
🔸 Network: ${tokenPriceData.network}
🔸 Token Name: ${tokenPriceData.name}
🔸 Contract: ${tokenPriceData.tokenAddress || 'N/A'}`;

    const responseContent = {
      action: 'GET_TOKEN_PRICE_DATA',
      symbol: content.symbol,
      network: tokenPriceData.network,
      price: tokenPriceData.price,
      name: tokenPriceData.name,
      tokenAddress: tokenPriceData.tokenAddress,
      currency,
    };

    logger.debug('📤 Sending successful response to callback', {
      responseLength: responseText.length,
      responseContent,
    });

    callback?.({
      text: responseText,
      content: responseContent,
    });

    const totalDuration = Date.now() - startTime;
    logger.debug('🎉 Price handling completed successfully', {
      totalDuration: `${totalDuration}ms`,
    });
  } catch (error) {
    const errorDuration = Date.now() - startTime;
    logger.error('💥 Error in handleCurrentPrice', {
      error: error instanceof Error ? error.message : String(error),
      errorStack: error instanceof Error ? error.stack : undefined,
      symbol: content.symbol,
      network,
      duration: `${errorDuration}ms`,
    });

    // Re-throw to be handled by the main handler
    throw error;
  }
}

// Helper functions from original actions
async function fetchTokenPrice(
  config: ZapperConfig,
  symbol: string,
  network: string
): Promise<TokenPriceData | null> {
  const startTime = Date.now();
  logger.debug('🌍 Starting token price fetch from Zapper API', {
    symbol,
    network,
    hasApiKey: !!config.apiKey,
    apiUrl: config.apiUrl,
  });

  try {
    // Step 1: Initialize Zapper client
    logger.debug('🔧 Step 1: Initializing Zapper client');
    if (!isZapperClientInitialized()) {
      logger.debug('🔄 Zapper client not initialized - initializing now');
      initializeZapperClient(config.apiKey, config.apiUrl);
      logger.debug('✅ Zapper client initialized successfully');
    } else {
      logger.debug('✅ Zapper client already initialized');
    }

    const client = getZapperClient();
    if (!client) {
      logger.error('❌ Failed to get Zapper client instance');
      throw new Error('Zapper client is not available');
    }

    // Step 2: Network mapping
    logger.debug('🌐 Step 2: Mapping network to chain ID');
    const networkToChainId: { [key: string]: number } = {
      ethereum: 1,
      polygon: 137,
      arbitrum: 42161,
      optimism: 10,
      base: 8453,
      avalanche: 43114,
      'binance-smart-chain': 56,
      bsc: 56,
      fantom: 250,
      mainnet: 1,
      eth: 1,
      matic: 137,
      arb: 42161,
      op: 10,
      avax: 43114,
      bnb: 56,
      ftm: 250,
    };

    const normalized = network.toLowerCase().trim();
    const chainId = networkToChainId[normalized] || 1;

    logger.debug('✅ Network mapping completed', {
      originalNetwork: network,
      normalizedNetwork: normalized,
      chainId,
      isDefaultChain:
        chainId === 1 &&
        normalized !== 'ethereum' &&
        normalized !== 'mainnet' &&
        normalized !== 'eth',
    });

    // Step 3: Prepare GraphQL query
    logger.debug('📝 Step 3: Preparing GraphQL query');
    const query = `
      query TokenPriceViaPortfolio($chainIds: [Int!], $symbolLike: String!) {
        portfolioV2(addresses: ["0x0000000000000000000000000000000000000000"], chainIds: $chainIds) {
          tokenBalances {
            byToken(first: 100, filters: { symbolLike: $symbolLike }) {
              edges {
                node {
                  symbol
                  name
                  balance
                  balanceUSD
                  network {
                    name
                  }
                  tokenAddress
                  price
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      chainIds: [chainId],
      symbolLike: symbol.toUpperCase(),
    };

    logger.debug('✅ GraphQL query prepared', {
      queryLength: query.length,
      variables,
    });

    // Step 4: Execute API call
    logger.debug('📡 Step 4: Executing Zapper API call');
    const queryStartTime = Date.now();

    const result = (await client.query(query, variables, {
      cacheTTL: 300000, // 5-minute cache for price data
      useCache: true,
      timeout: 15000,
      retryAttempts: 2,
    })) as ZapperTokenResponse;

    const queryDuration = Date.now() - queryStartTime;
    logger.debug('✅ Zapper API call completed', {
      queryDuration: `${queryDuration}ms`,
      hasResult: !!result,
      hasData: !!result?.data,
    });

    // Step 5: Process API response
    logger.debug('🔍 Step 5: Processing API response');
    const tokenEdges = result.data?.portfolioV2?.tokenBalances?.byToken?.edges || [];

    logger.debug('📋 Token edges retrieved', {
      edgeCount: tokenEdges.length,
      hasEdges: tokenEdges.length > 0,
    });

    if (tokenEdges.length === 0) {
      logger.warn('⚠️ No token edges found in API response', {
        symbol,
        network,
        chainId,
        queryDuration: `${queryDuration}ms`,
      });
      return null;
    }

    // Step 6: Find matching token
    logger.debug('🔎 Step 6: Finding matching token');
    const matchingToken = tokenEdges.find((edge: TokenEdge) => {
      const isMatch = edge.node.symbol.toUpperCase() === symbol.toUpperCase();
      if (isMatch) {
        logger.debug(`✅ Found matching token: ${edge.node.symbol}`);
      }
      return isMatch;
    });

    if (!matchingToken) {
      logger.warn('⚠️ No matching token found', {
        symbol,
        searchedSymbol: symbol.toUpperCase(),
        availableSymbols: tokenEdges.map((edge: TokenEdge) => edge.node.symbol),
        network,
        chainId,
      });
      return null;
    }

    const tokenData = matchingToken.node;
    logger.debug('✅ Token data extracted', {
      symbol: tokenData.symbol,
      name: tokenData.name,
      price: tokenData.price,
      network: tokenData.network?.name,
      tokenAddress: tokenData.tokenAddress,
      hasBalance: !!tokenData.balance,
      hasBalanceUSD: !!tokenData.balanceUSD,
    });

    // Step 7: Format return data
    const returnData: TokenPriceData = {
      price: tokenData.price || 0,
      priceChange24h: 0, // Zapper API doesn't provide 24h change in this query
      marketCap: 0, // Not available in this endpoint
      volume24h: 0, // Not available in this endpoint
      symbol: tokenData.symbol,
      name: tokenData.name,
      network: tokenData.network?.name || network,
      tokenAddress: tokenData.tokenAddress,
    };

    const totalDuration = Date.now() - startTime;
    logger.debug('🎉 Token price fetch completed successfully', {
      totalDuration: `${totalDuration}ms`,
      returnData,
    });

    return returnData;
  } catch (error) {
    const errorDuration = Date.now() - startTime;
    logger.error('💥 Token price fetch failed with error', {
      error: error instanceof Error ? error.message : String(error),
      errorStack: error instanceof Error ? error.stack : undefined,
      symbol,
      network,
      duration: `${errorDuration}ms`,
      errorType: error?.constructor?.name || 'Unknown',
    });

    // Log specific error types for better debugging
    if (error instanceof Error) {
      if (error.message.includes('timeout')) {
        logger.error('⏰ Zapper API timeout occurred - consider increasing timeout');
      } else if (error.message.includes('network')) {
        logger.error('🌐 Network error occurred - check connectivity');
      } else if (
        error.message.includes('authentication') ||
        error.message.includes('unauthorized')
      ) {
        logger.error('🔐 Authentication error - check API key');
      } else if (error.message.includes('rate limit')) {
        logger.error('📊 Rate limit exceeded - reduce request frequency');
      }
    }

    throw error;
  }
}
